import { LitElement, html, nothing } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import '@ui5/webcomponents/dist/Dialog'
import style from './style/zaptic-popup-message-modal-style'
import type { IconName } from './helpers/icons'
import type { ButtonAttributes } from './zaptic-button'

export enum Events {
    onCancel = 'onCancel',
    onSubmit = 'onSubmit',
    onOptionalButtonClick = 'onOptionalButtonClick',
}

export interface CustomEvents {
    [Events.onCancel]: CustomEvent<void>
    [Events.onSubmit]: CustomEvent<void>
    [Events.onOptionalButtonClick]: CustomEvent<void>
}

@customElement('zaptic-popup-message-modal')
export class PopupMessageModal extends LitElement {
    static styles = style

    @property()
    heading = ''

    @property()
    message = ''

    @property({ attribute: 'icon-name' })
    iconName?: IconName

    @property({ attribute: 'cancel-label' })
    cancelLabel = window.i18n('appcore:common.cancel')

    @property({ attribute: 'submit-label' })
    submitLabel = window.i18n('appcore:common.continue')

    @property({ attribute: 'submit-test-id' })
    submitTestId?: string

    // Optional property for adding additional button between submit and cancel buttons
    @property({ type: Object, attribute: 'optional-button' })
    optionalButton?: {
        type: ButtonAttributes['type']
        size: ButtonAttributes['size']
        label: string
    } = null

    @property({ type: Boolean, attribute: 'is-open' })
    isOpen = false

    @property({ type: Boolean, attribute: 'is-dangerous' })
    isDangerous = false

    @property({ type: Boolean, attribute: 'can-cancel' })
    canCancel = true

    @property({ type: Boolean, attribute: 'can-submit' })
    canSubmit = true

    /**  needed when placing dialog in nested z-index */
    @property({ type: Boolean, attribute: 'has-custom-overlay' })
    hasCustomOverlay = false

    private onClickCancelButton() {
        const event = new CustomEvent(Events.onCancel, { composed: true })
        this.dispatchEvent(event)
    }

    private onClickSubmitButton() {
        const event = new CustomEvent(Events.onSubmit, { composed: true })
        this.dispatchEvent(event)
    }

    private onClickOptionalButton() {
        const event = new CustomEvent(Events.onOptionalButtonClick, { composed: true })
        this.dispatchEvent(event)
    }

    connectedCallback() {
        super.connectedCallback()
        this.addEventListener('onClose', this.onClickCancelButton)
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        this.removeEventListener('onClose', this.onClickCancelButton)
    }

    render() {
        const submitType = this.isDangerous ? 'negative' : 'primary'

        const footerClasses = {
            footer: true,
            'footer--grid': this.canCancel && this.canSubmit,
        }

        return html`
            <zaptic-modal-v2
                .isOpen="${this.isOpen}"
                class="${classMap({ 'has-custom-overlay': this.hasCustomOverlay && this.isOpen })}"
            >
                <div slot="content">
                    <zaptic-centered-message
                        heading="${window.i18n(this.heading)}"
                        message="${window.i18n(this.message)}"
                        iconName="${this.iconName}"
                    ></zaptic-centered-message>
                </div>

                <div class="${classMap(footerClasses)}" slot="footer">
                    ${this.canSubmit
                        ? html`<zaptic-button
                              type="${submitType}"
                              size="large"
                              @click="${this.onClickSubmitButton}"
                              test-id="${this.submitTestId}"
                          >
                              ${window.i18n(this.submitLabel)}
                          </zaptic-button>`
                        : nothing}
                    ${this.optionalButton
                        ? html`<zaptic-button
                              type="${this.optionalButton.type}"
                              size="${this.optionalButton.size}"
                              @click="${this.onClickOptionalButton}"
                          >
                              ${window.i18n(this.optionalButton.label)}
                          </zaptic-button>`
                        : nothing}
                    ${this.canCancel
                        ? html`<zaptic-button type="secondary" size="large" @click="${this.onClickCancelButton}">
                              ${window.i18n(this.cancelLabel)}
                          </zaptic-button>`
                        : nothing}
                </div>
            </zaptic-modal-v2>
        `
    }
}
